import React, { useState, useCallback, useEffect, useRef } from 'react';
import axiosInstance from '../../api/axiosConfig';
import {
  Box, Typography, Tabs, Tab, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Snackbar, Alert, useTheme,
  Card, CardContent, CircularProgress, Stack, Paper, Divider, Avatar,
  IconButton, Tooltip, Chip, Skeleton, Grid, Fade, Backdrop
} from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import Lottie from 'lottie-react';

// Icons
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';
import SchoolRoundedIcon from '@mui/icons-material/SchoolRounded';
import PeopleRoundedIcon from '@mui/icons-material/PeopleRounded';
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded';
import PhotoCameraRoundedIcon from '@mui/icons-material/PhotoCameraRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import ClearRoundedIcon from '@mui/icons-material/ClearRounded';
import SmsRoundedIcon from '@mui/icons-material/SmsRounded';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import PhoneIphoneRoundedIcon from '@mui/icons-material/PhoneIphoneRounded';

// Animations
import successAnimation from '../../assets/icons/sucess.json';
import sendingAnimation from '../../assets/icons/message-sending.json';

import { STORAGE_KEYS } from '../../constants';
import PageContainer from '../common/PageContainer';

const logger = {
  info: (...args) => console.log('[INFO]', ...args),
  warn: (...args) => console.warn('[WARN]', ...args),
  error: (...args) => console.error('[ERROR]', ...args),
};

const MAX_FILE_SIZE_MB = 5;

// --- STYLED COMPONENTS ---

const PageHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(4),
  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
}));

const SegmentedTabs = styled(Tabs)(({ theme }) => ({
  minHeight: 44,
  backgroundColor: alpha(theme.palette.divider, 0.08),
  borderRadius: 6,
  padding: 4,
  display: 'inline-flex',
  '& .MuiTabs-indicator': { display: 'none' },
}));

const SegmentedTab = styled(Tab)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 700,
  fontSize: '0.95rem',
  minHeight: 36,
  borderRadius: 8,
  color: theme.palette.text.secondary,
  margin: '0 2px',
  transition: 'all 0.2s ease',
  '&.Mui-selected': {
    color: theme.palette.primary.main,
    backgroundColor: theme.palette.background.paper,
    boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.08)}`,
  },
  '&:hover:not(.Mui-selected)': {
    backgroundColor: alpha(theme.palette.background.paper, 0.5),
  },
}));

const ChannelCard = styled(Paper)(({ theme, selected, brandcolor }) => ({
  flex: 1,
  padding: theme.spacing(2),
  borderRadius: 6,
  border: `2px solid ${selected ? brandcolor : alpha(theme.palette.divider, 0.4)}`,
  backgroundColor: selected ? alpha(brandcolor, 0.04) : theme.palette.background.paper,
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: theme.spacing(1),
  boxShadow: selected ? `0 8px 20px ${alpha(brandcolor, 0.15)}` : 'none',
  '&:hover': {
    borderColor: selected ? brandcolor : alpha(brandcolor, 0.4),
    transform: 'translateY(-2px)',
  },
}));

const UploadZone = styled(Box)(({ theme, active }) => ({
  border: `2px dashed ${active ? theme.palette.primary.main : alpha(theme.palette.divider, 0.8)}`,
  borderRadius: 6,
  padding: theme.spacing(3),
  backgroundColor: active ? alpha(theme.palette.primary.main, 0.04) : alpha(theme.palette.divider, 0.02),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(1),
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  position: 'relative',
  overflow: 'hidden',
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
    borderColor: theme.palette.primary.main,
  },
}));

const HiddenInput = styled('input')({
  position: 'absolute', width: '100%', height: '100%', opacity: 0, cursor: 'pointer', zIndex: 2
});

// --- SMARTPHONE PREVIEW COMPONENT ---
const PhoneFrame = styled(Box)(({ theme }) => ({
  width: 320,
  height: 620,
  margin: '0 auto',
  border: `12px solid ${theme.palette.mode === 'dark' ? '#333' : '#111'}`,
  borderRadius: 40,
  backgroundColor: theme.palette.mode === 'dark' ? '#000' : '#f4f4f5',
  position: 'relative',
  overflow: 'hidden',
  boxShadow: `0 24px 48px ${alpha(theme.palette.common.black, 0.2)}`,
  display: 'flex',
  flexDirection: 'column',
  '&::before': { // Notch
    content: '""', position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)',
    width: 120, height: 24, backgroundColor: theme.palette.mode === 'dark' ? '#333' : '#111',
    borderBottomLeftRadius: 16, borderBottomRightRadius: 16, zIndex: 10
  }
}));

const getBrandColors = (theme) => ({
  sms: theme.palette.info.main,
  whatsapp: '#25D366',
  inApp: theme.palette.primary.main,
});

// --- MAIN COMPONENT ---

const SendMessage = () => {
  const theme = useTheme();
  const schoolId = localStorage.getItem(STORAGE_KEYS.SCHOOL_ID);
  
  const [activeTab, setActiveTab] = useState(0);
  const[notificationMethod, setNotificationMethod] = useState('inApp');
  const [message, setMessage] = useState('');
  const [titleText, setTitleText] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState('');
  
  const[balance, setBalance] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(true);
  const [apiLoading, setApiLoading] = useState(false);
  const[animationState, setAnimationState] = useState('idle');
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const brandColors = getBrandColors(theme);
  const INSUFFICIENT_BALANCE = "Insufficient balance. Please add funds.";

  const getRecipientType = useCallback(() => {
    switch (activeTab) {
      case 0: return 'students';
      case 1: return 'employees';
      case 2: return 'teachers';
      default: return 'students';
    }
  }, [activeTab]);

  const resetFormFields = useCallback(() => {
    setMessage(''); setTitleText('');
    if (imagePreviewUrl) URL.revokeObjectURL(imagePreviewUrl);
    setSelectedImage(null); setImagePreviewUrl('');
  },[imagePreviewUrl]);

  const resetFull = useCallback(() => {
    resetFormFields(); setAnimationState('idle'); setApiLoading(false); setConfirmDialogOpen(false);
  }, [resetFormFields]);

  const fetchBalance = useCallback(async () => {
    if (!schoolId) { setBalanceLoading(false); setBalance(0); return; }
    setBalanceLoading(true);
    try {
        const response = await axiosInstance.get(`/api/account-balance/current/${schoolId}`);
        setBalance(response.data?.balance ?? 0);
    } catch (error) {
        setSnackbar({ open: true, message: 'Could not fetch account balance.', severity: 'error' });
        setBalance(null);
    } finally { setBalanceLoading(false); }
  }, [schoolId]);

  useEffect(() => { fetchBalance(); },[fetchBalance]);

  const isBalanceSufficient = useCallback((method) => {
    if (method === 'sms' || method === 'whatsapp') return balance !== null && balance > 0;
    return true;
  }, [balance]);

  const handleMethodSelect = (method) => {
    if (apiLoading) return;
    setNotificationMethod(method);
    if (method === 'sms') setTitleText(''); // SMS doesn't use title
  };

  const handleImageChange = (event) => {
     if (apiLoading) return;
     const file = event.target.files?.[0];
     if (file) {
        if (!file.type.startsWith('image/')) {
            setSnackbar({ open: true, message: 'Please select a valid image (PNG, JPG).', severity: 'warning' }); return;
        }
        if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
            setSnackbar({ open: true, message: `Image size must be under ${MAX_FILE_SIZE_MB}MB.`, severity: 'warning' }); return;
        }
        setSelectedImage(file);
        if (imagePreviewUrl) URL.revokeObjectURL(imagePreviewUrl);
        setImagePreviewUrl(URL.createObjectURL(file));
     }
  };

  const clearSelectedImage = (e) => {
    if(e) e.preventDefault();
    if (imagePreviewUrl) URL.revokeObjectURL(imagePreviewUrl);
    setSelectedImage(null); setImagePreviewUrl('');
  };

  const handleSendConfirmed = async () => {
    if (apiLoading) return;
    setApiLoading(true); setAnimationState('sending');
    let finalImageUrl = null;
    const recipientType = getRecipientType();

    try {
        if (notificationMethod === 'inApp' && selectedImage) {
            finalImageUrl = await axiosInstance.uploadImage(selectedImage, 'IMAGES', schoolId, 'Notification Image', 'Notifications');
            if (!finalImageUrl) throw new Error('Image upload failed.');
        }

        let endpoint = ''; let payload = {};
        if (notificationMethod === 'inApp') {
            endpoint = '/api/notifications/broadcast';
            payload = { title: titleText, body: message, recipientType: recipientType.toUpperCase(), ...(finalImageUrl && { imageUrl: finalImageUrl }) };
        } else {
            endpoint = `/api/messaging/${notificationMethod}/send/bulk?sendToAll=true&recipientType=${recipientType}`;
            payload = { message: message };
        }

        await axiosInstance.post(endpoint, payload);
        
        setAnimationState('success'); fetchBalance();
        setTimeout(() => { setSnackbar({ open: true, message: 'Broadcast sent successfully!', severity: 'success' }); resetFull(); }, 2000);

    } catch (error) {
        logger.error(error);
        setAnimationState('error'); 
        setSnackbar({ open: true, message: error.response?.data?.message || 'Failed to send broadcast.', severity: 'error' });
        setApiLoading(false);
    }
  };

  const maxChars = notificationMethod === 'sms' ? 160 : 1000;
  const isOverLimit = message.length > maxChars;
  const disableSend = !message.trim() || isOverLimit || (notificationMethod === 'inApp' && !titleText.trim()) || !isBalanceSufficient(notificationMethod);

  // --- LIVE PREVIEW RENDERER ---
  const renderLivePreview = () => {
    const time = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

    if (notificationMethod === 'whatsapp') {
      return (
        <Box sx={{ height: '100%', bgcolor: '#efeae2', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ bgcolor: '#075e54', color: '#fff', pt: 5, pb: 1.5, px: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar sx={{ width: 32, height: 32 }} />
            <Box>
              <Typography variant="subtitle2" fontWeight={600} lineHeight={1}>School Update</Typography>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>Official Account</Typography>
            </Box>
          </Box>
          <Box sx={{ p: 2, flex: 1, overflowY: 'auto' }}>
            <Box sx={{ bgcolor: '#dcf8c6', p: 1.5, borderRadius: '8px 8px 8px 0', maxWidth: '85%', ml: 'auto', boxShadow: 1, position: 'relative' }}>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: '#111' }}>
                {message || <span style={{ opacity: 0.4 }}>Type a message...</span>}
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', textAlign: 'right', mt: 0.5, color: 'rgba(0,0,0,0.45)', fontSize: '0.65rem' }}>{time}</Typography>
            </Box>
          </Box>
        </Box>
      );
    }

    if (notificationMethod === 'sms') {
      return (
        <Box sx={{ height: '100%', bgcolor: '#fff', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ bgcolor: '#f4f4f4', pt: 5, pb: 2, textAlign: 'center', borderBottom: '1px solid #ddd' }}>
             <Avatar sx={{ width: 40, height: 40, bgcolor: '#ccc', margin: '0 auto', mb: 0.5 }}><SmsRoundedIcon /></Avatar>
             <Typography variant="caption" fontWeight={600} color="text.secondary">Skhool Alert</Typography>
          </Box>
          <Box sx={{ p: 2, flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <Typography variant="caption" sx={{ width: '100%', textAlign: 'center', mb: 2, color: 'text.disabled' }}>Text Message • Today {time}</Typography>
            <Box sx={{ bgcolor: '#e9e9eb', color: '#000', p: 1.5, borderRadius: '16px 16px 16px 4px', maxWidth: '85%' }}>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {message || <span style={{ opacity: 0.4 }}>Type a message...</span>}
              </Typography>
            </Box>
          </Box>
        </Box>
      );
    }

    // In-App Preview (Lock Screen Style)
    return (
      <Box sx={{ height: '100%', background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.info.dark})`, display: 'flex', flexDirection: 'column', pt: 8, px: 2 }}>
        <Typography variant="h3" fontWeight={200} color="#fff" textAlign="center" mb={3}>{time}</Typography>
        <Paper elevation={4} sx={{ borderRadius: 4, bgcolor: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(10px)', p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box sx={{ bgcolor: brandColors.inApp, borderRadius: 1.5, p: 0.5, display: 'flex' }}><SchoolRoundedIcon sx={{ color: '#fff', fontSize: 16 }} /></Box>
            <Typography variant="caption" fontWeight={700} sx={{ opacity: 0.7, flex: 1 }}>SKHOOL APP</Typography>
            <Typography variant="caption" sx={{ opacity: 0.6 }}>now</Typography>
          </Stack>
          <Box>
            <Typography variant="subtitle2" fontWeight={800} lineHeight={1.2} sx={{ wordBreak: 'break-word', mb: 0.5 }}>
              {titleText || <span style={{ opacity: 0.4 }}>Notification Title</span>}
            </Typography>
            <Typography variant="body2" sx={{ wordBreak: 'break-word', lineHeight: 1.3, opacity: 0.8, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
              {message || <span style={{ opacity: 0.4 }}>Message body will appear here...</span>}
            </Typography>
          </Box>
          {imagePreviewUrl && (
            <Box sx={{ mt: 1, width: '100%', height: 120, borderRadius: 2, backgroundImage: `url(${imagePreviewUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
          )}
        </Paper>
      </Box>
    );
  };

  return (
    <PageContainer>
      
      {/* HEADER SECTION */}
      <PageHeader>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, mb: 0.5 }}>Broadcast Message</Typography>
          <Typography color="text.secondary">Send instant updates across multiple channels.</Typography>
        </Box>
        
        <Stack direction="row" alignItems="center" spacing={2}>
           {balanceLoading ? <Skeleton variant="rounded" width={120} height={36} /> : (
             <Chip 
               icon={<AccountBalanceWalletRoundedIcon />} 
               label={`${balance?.toFixed(0) || 0} Credits`} 
               color={balance > 10 ? 'success' : balance > 0 ? 'warning' : 'error'} 
               variant="filled" 
               sx={{ fontWeight: 700, borderRadius: 2 }} 
             />
           )}
        </Stack>
      </PageHeader>

      <Grid container spacing={2}>
        
        {/* COMPOSER PANEL (LEFT) */}
        <Grid item xs={12} lg={7}>
          
          {/* Step 1: Audience */}
          <Box mb={4}>
            <Typography variant="subtitle2" fontWeight={700} color="text.secondary" mb={1.5} textTransform="uppercase" letterSpacing={1}>1. Select Audience</Typography>
            <SegmentedTabs value={activeTab} onChange={(e, val) => setActiveTab(val)}>
              <SegmentedTab label="Students" icon={<SchoolRoundedIcon fontSize="small"/>} iconPosition="start" />
              <SegmentedTab label="Employees" icon={<PeopleRoundedIcon fontSize="small"/>} iconPosition="start" />
              <SegmentedTab label="Teachers" icon={<BadgeRoundedIcon fontSize="small"/>} iconPosition="start" />
            </SegmentedTabs>
          </Box>

          {/* Step 2: Channel */}
          <Box mb={4}>
            <Typography variant="subtitle2" fontWeight={700} color="text.secondary" mb={1.5} textTransform="uppercase" letterSpacing={1}>2. Delivery Channel</Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <ChannelCard selected={notificationMethod === 'inApp'} brandcolor={brandColors.inApp} onClick={() => handleMethodSelect('inApp')}>
                <NotificationsActiveRoundedIcon sx={{ color: notificationMethod === 'inApp' ? brandColors.inApp : 'text.secondary' }} />
                <Box>
                  <Typography variant="subtitle1" fontWeight={700}>In-App Push</Typography>
                  <Typography variant="caption" color="text.secondary">Free • Rich Media</Typography>
                </Box>
              </ChannelCard>
              <ChannelCard selected={notificationMethod === 'sms'} brandcolor={brandColors.sms} onClick={() => handleMethodSelect('sms')}>
                <SmsRoundedIcon sx={{ color: notificationMethod === 'sms' ? brandColors.sms : 'text.secondary' }} />
                <Box>
                  <Typography variant="subtitle1" fontWeight={700}>SMS Text</Typography>
                  <Typography variant="caption" color="text.secondary">Uses Credits • 160 Chars</Typography>
                </Box>
              </ChannelCard>
              <ChannelCard selected={notificationMethod === 'whatsapp'} brandcolor={brandColors.whatsapp} onClick={() => handleMethodSelect('whatsapp')}>
                <WhatsAppIcon sx={{ color: notificationMethod === 'whatsapp' ? brandColors.whatsapp : 'text.secondary' }} />
                <Box>
                  <Typography variant="subtitle1" fontWeight={700}>WhatsApp</Typography>
                  <Typography variant="caption" color="text.secondary">Uses Credits • Fast</Typography>
                </Box>
              </ChannelCard>
            </Stack>
          </Box>

          {/* Step 3: Compose */}
          <Box mb={4}>
            <Typography variant="subtitle2" fontWeight={700} color="text.secondary" mb={1.5} textTransform="uppercase" letterSpacing={1}>3. Compose Message</Typography>
            <Card sx={{ borderRadius: 4, border: `1px solid ${theme.palette.divider}`, boxShadow: 'none' }}>
              <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
                
                {notificationMethod === 'inApp' && (
                  <TextField 
                    label="Notification Title" variant="outlined" fullWidth required 
                    value={titleText} onChange={(e) => setTitleText(e.target.value)} 
                    placeholder="E.g. Tomorrow is a Holiday!"
                    InputProps={{ sx: { borderRadius: 2, fontWeight: 600 } }}
                  />
                )}

                <Box>
                  <TextField 
                    label="Message Body" multiline rows={5} variant="outlined" fullWidth required 
                    value={message} onChange={(e) => setMessage(e.target.value)} 
                    placeholder="Type your main message here..."
                    error={isOverLimit}
                    InputProps={{ sx: { borderRadius: 2 } }}
                  />
                  <Stack direction="row" justifyContent="flex-end" mt={1}>
                    <Typography variant="caption" fontWeight={600} color={isOverLimit ? 'error.main' : 'text.secondary'}>
                      {message.length} / {maxChars} characters
                    </Typography>
                  </Stack>
                </Box>

                {notificationMethod === 'inApp' && (
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700} mb={1}>Attach Image (Optional)</Typography>
                    {imagePreviewUrl ? (
                      <Box sx={{ position: 'relative', borderRadius: 1.5, overflow: 'hidden', border: `1px solid ${theme.palette.divider}`, height: 160 }}>
                        <img src={imagePreviewUrl} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        <IconButton size="small" onClick={clearSelectedImage} sx={{ position: 'absolute', top: 8, right: 8, bgcolor: 'rgba(0,0,0,0.6)', color: '#fff', '&:hover': { bgcolor: 'error.main' } }}>
                          <ClearRoundedIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ) : (
                      <UploadZone>
                        <HiddenInput type="file" accept="image/*" onChange={handleImageChange} />
                        <PhotoCameraRoundedIcon color="primary" sx={{ fontSize: 32, opacity: 0.5 }} />
                        <Typography variant="body2" fontWeight={600}>Click or Drag image here</Typography>
                        <Typography variant="caption" color="text.secondary">PNG, JPG up to {MAX_FILE_SIZE_MB}MB</Typography>
                      </UploadZone>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Box>

          <Button 
            fullWidth variant="contained" size="large" 
            onClick={() => setConfirmDialogOpen(true)}
            disabled={disableSend}
            startIcon={<SendRoundedIcon />}
            sx={{ py: 1.8, borderRadius: 1.5, fontWeight: 800, fontSize: '1.05rem', bgcolor: brandColors[notificationMethod], '&:hover': { bgcolor: alpha(brandColors[notificationMethod], 0.8) } }}
          >
            Broadcast to {getRecipientType()}
          </Button>

        </Grid>

        {/* LIVE PREVIEW PANEL (RIGHT) */}
        <Grid item xs={12} lg={5} sx={{ display: { xs: 'none', lg: 'block' } }}>
          <Box sx={{ position: 'sticky', top: 24 }}>
            <Stack direction="row" alignItems="center" spacing={1} justifyContent="center" mb={3}>
              <PhoneIphoneRoundedIcon color="action" />
              <Typography variant="subtitle1" fontWeight={700} color="text.secondary" textTransform="uppercase" letterSpacing={1}>Live Preview</Typography>
            </Stack>
            <Fade in={true} timeout={500}>
              <PhoneFrame>
                {renderLivePreview()}
              </PhoneFrame>
            </Fade>
          </Box>
        </Grid>
      </Grid>

      {/* CONFIRMATION DIALOG */}
      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)} PaperProps={{ sx: { borderRadius: 4, p: 1, minWidth: 400 } }}>
        <DialogTitle sx={{ fontWeight: 800, textAlign: 'center', pb: 1 }}>Confirm Broadcast</DialogTitle>
        <DialogContent sx={{ textAlign: 'center' }}>
          <Typography variant="body1" mb={2}>
            You are about to send a <strong>{notificationMethod === 'inApp' ? 'Mobile Push' : notificationMethod.toUpperCase()}</strong> to all <strong>{getRecipientType()}</strong>.
          </Typography>
          {(notificationMethod === 'sms' || notificationMethod === 'whatsapp') && ( 
            <Alert severity="warning" sx={{ mb: 2, borderRadius: 2, textAlign: 'left' }}>
              This will deduct credits from your balance.
            </Alert> 
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
          <Button onClick={() => setConfirmDialogOpen(false)} color="inherit" sx={{ fontWeight: 700 }}>Cancel</Button>
          <Button onClick={() => { setConfirmDialogOpen(false); handleSendConfirmed(); }} variant="contained" sx={{ px: 4, borderRadius: 2, fontWeight: 700, bgcolor: brandColors[notificationMethod] }}>
            Yes, Send Now
          </Button>
        </DialogActions>
      </Dialog>

      {/* SENDING / SUCCESS ANIMATION OVERLAY */}
      <Backdrop open={animationState !== 'idle'} sx={{ zIndex: 9999, flexCol: true, bgcolor: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(8px)' }}>
        <Box sx={{ textAlign: 'center' }}>
          {animationState === 'sending' && (
            <>
              <Lottie animationData={sendingAnimation} style={{ width: 250, height: 250, margin: '0 auto' }} />
              <Typography variant="h5" fontWeight={800} color="primary" mt={-2}>Broadcasting...</Typography>
              <Typography variant="body1" color="text.secondary" mt={1}>Please don't close this window.</Typography>
            </>
          )}
          {animationState === 'success' && (
            <>
              <Lottie animationData={successAnimation} loop={false} style={{ width: 250, height: 250, margin: '0 auto' }} />
              <Typography variant="h5" fontWeight={800} color="success.main" mt={-2}>Success!</Typography>
              <Typography variant="body1" color="text.secondary" mt={1}>Messages dispatched to {getRecipientType()}.</Typography>
            </>
          )}
          {animationState === 'error' && (
            <>
              <ErrorRoundedIcon color="error" sx={{ fontSize: 100, mb: 2 }} />
              <Typography variant="h5" fontWeight={800} color="error.main">Failed to Send</Typography>
              <Typography variant="body1" color="text.secondary" mt={1} mb={3}>{snackbar.message}</Typography>
              <Button variant="contained" color="primary" onClick={() => setAnimationState('idle')} sx={{ borderRadius: 2, fontWeight: 700 }}>Back to Editor</Button>
            </>
          )}
        </Box>
      </Backdrop>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(s => ({...s, open: false}))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} variant="filled" sx={{ borderRadius: 1.5, fontWeight: 600 }}>{snackbar.message}</Alert>
      </Snackbar>

    </PageContainer>
  );
};

export default SendMessage;
